import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2,
  Search,
  Plus,
  Edit2,
  Trash2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Users,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
  Download,
  Eye,
  Map as MapIcon
} from 'lucide-react';
import fieldTeamApi from '../../services/fieldTeamApi';
import './Clients.css';

// Status options
const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'meeting_scheduled', label: 'Meeting Scheduled' },
  { value: 'trial', label: 'Trial' },
  { value: 'converted', label: 'Converted' },
  { value: 'lost', label: 'Lost' }
];

// Interest level options
const INTEREST_OPTIONS = [
  { value: 'none', label: 'None', color: '#6b7280' },
  { value: 'low', label: 'Low', color: '#f59e0b' },
  { value: 'medium', label: 'Medium', color: '#eab308' },
  { value: 'high', label: 'High', color: '#22c55e' },
  { value: 'very_high', label: 'Very High', color: '#10b981' }
];

// Company size options
const SIZE_OPTIONS = [
  { value: 'small', label: 'Small (1-10)' },
  { value: 'medium', label: 'Medium (11-50)' },
  { value: 'large', label: 'Large (51-200)' },
  { value: 'enterprise', label: 'Enterprise (200+)' }
];

function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAllMembers, setShowAllMembers] = useState(true);

  // Modal states
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        all_members: showAllMembers ? 1 : undefined
      };

      const response = await fieldTeamApi.getClients(params);
      if (response.data.success) {
        setClients(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          totalPages: response.data.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchQuery, statusFilter, showAllMembers]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // View client details
  const handleViewClient = async (client) => {
    try {
      const response = await fieldTeamApi.getClient(client.id);
      if (response.data.success) {
        setSelectedClient(response.data.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Failed to fetch client details:', error);
    }
  };

  // Edit client
  const handleEditClient = (client) => {
    setEditingClient({ ...client });
    setShowEditModal(true);
  };

  // Save client
  const handleSaveClient = async () => {
    if (!editingClient) return;
    setSaving(true);
    try {
      if (editingClient.id) {
        await fieldTeamApi.updateAdminClient(editingClient.id, editingClient);
      } else {
        await fieldTeamApi.createClient(editingClient);
      }
      setShowEditModal(false);
      setEditingClient(null);
      fetchClients();
    } catch (error) {
      console.error('Failed to save client:', error);
      alert('Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  // Delete client
  const handleDeleteClient = async (client) => {
    if (!confirm(`Are you sure you want to delete ${client.company_name}?`)) return;
    try {
      await fieldTeamApi.deleteAdminClient(client.id);
      fetchClients();
    } catch (error) {
      console.error('Failed to delete client:', error);
      alert('Failed to delete client');
    }
  };

  // Export to CSV
  const handleExport = () => {
    const headers = ['Company', 'Contact', 'Phone', 'Email', 'Address', 'Status', 'Interest', 'Created'];
    const rows = clients.map(c => [
      c.company_name,
      c.contact_name || '',
      c.contact_phone || '',
      c.contact_email || '',
      c.address || '',
      c.status,
      c.interest_level || '',
      new Date(c.created_at).toLocaleDateString()
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Get status badge class
  const getStatusClass = (status) => {
    const classes = {
      prospect: 'status-prospect',
      contacted: 'status-contacted',
      meeting_scheduled: 'status-meeting',
      trial: 'status-trial',
      converted: 'status-converted',
      lost: 'status-lost'
    };
    return classes[status] || 'status-prospect';
  };

  // Get interest badge
  const getInterestBadge = (interest) => {
    const option = INTEREST_OPTIONS.find(o => o.value === interest);
    if (!option) return null;
    return (
      <span className="interest-badge" style={{ backgroundColor: `${option.color}20`, color: option.color }}>
        {option.label}
      </span>
    );
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="clients-page">
      {/* Header */}
      <div className="field-team-page-header">
        <h1>Clients</h1>
        <p>Manage client database and track relationships</p>
      </div>

      {/* Stats */}
      <div className="stat-blocks">
        <div className="stat-block">
          <div className="stat-icon"><Building2 size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{pagination.total}</span>
            <span className="stat-label">Total Clients</span>
          </div>
        </div>
        <div className="stat-block">
          <div className="stat-icon"><Users size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{clients.filter(c => c.status === 'converted').length}</span>
            <span className="stat-label">Converted</span>
          </div>
        </div>
        <div className="stat-block">
          <div className="stat-icon"><Calendar size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{clients.filter(c => c.status === 'trial').length}</span>
            <span className="stat-label">In Trial</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="clients-toolbar">
        <div className="toolbar-left">
          <div className="search-box">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showAllMembers}
              onChange={(e) => setShowAllMembers(e.target.checked)}
            />
            All Members
          </label>
        </div>

        <div className="toolbar-right">
          <button className="btn btn-secondary" onClick={handleExport}>
            <Download size={14} />
            Export
          </button>
          <button className="btn btn-primary" onClick={() => { setEditingClient({}); setShowEditModal(true); }}>
            <Plus size={14} />
            Add Client
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="clients-table-container">
        <table className="clients-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Interest</th>
              <th>Meetings</th>
              <th>Last Meeting</th>
              <th>Created By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="loading-cell">Loading...</td>
              </tr>
            ) : clients.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-cell">No clients found</td>
              </tr>
            ) : (
              clients.map((client) => (
                <tr key={client.id}>
                  <td>
                    <div className="company-cell">
                      <Building2 size={14} />
                      <div>
                        <span className="company-name">{client.company_name}</span>
                        {client.address && (
                          <span className="company-address">{client.address}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      <span className="contact-name">{client.contact_name || '-'}</span>
                      {client.contact_phone && (
                        <span className="contact-detail"><Phone size={10} /> {client.contact_phone}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(client.status)}`}>
                      {client.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{getInterestBadge(client.interest_level) || '-'}</td>
                  <td className="centered">{client.meetings_count || 0}</td>
                  <td>{formatDate(client.last_meeting_date)}</td>
                  <td>{client.created_by_name}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="icon-btn" onClick={() => handleViewClient(client)} title="View">
                        <Eye size={14} />
                      </button>
                      <button className="icon-btn" onClick={() => handleEditClient(client)} title="Edit">
                        <Edit2 size={14} />
                      </button>
                      <button className="icon-btn danger" onClick={() => handleDeleteClient(client)} title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            <ChevronLeft size={14} />
          </button>
          <span className="pagination-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="pagination-btn"
            disabled={pagination.page === pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedClient && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedClient.company_name}</h2>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Company Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Company Name</label>
                    <span>{selectedClient.company_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Industry</label>
                    <span>{selectedClient.industry || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Company Size</label>
                    <span>{SIZE_OPTIONS.find(s => s.value === selectedClient.company_size)?.label || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <span className={`status-badge ${getStatusClass(selectedClient.status)}`}>
                      {selectedClient.status?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Contact Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Contact Name</label>
                    <span>{selectedClient.contact_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Phone</label>
                    <span>{selectedClient.contact_phone || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email</label>
                    <span>{selectedClient.contact_email || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Location</h3>
                <div className="detail-grid">
                  <div className="detail-item full-width">
                    <label>Address</label>
                    <span>{selectedClient.address || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Floor</label>
                    <span>{selectedClient.floor || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Office Number</label>
                    <span>{selectedClient.office_number || '-'}</span>
                  </div>
                </div>
              </div>

              {selectedClient.notes && (
                <div className="detail-section">
                  <h3>Notes</h3>
                  <p className="notes-text">{selectedClient.notes}</p>
                </div>
              )}

              {selectedClient.meetings && selectedClient.meetings.length > 0 && (
                <div className="detail-section">
                  <h3>Meeting History ({selectedClient.meetings.length})</h3>
                  <div className="meetings-list">
                    {selectedClient.meetings.map(meeting => (
                      <div key={meeting.id} className="meeting-item">
                        <div className="meeting-date">{formatDate(meeting.meeting_date)}</div>
                        <div className="meeting-info">
                          <span className="meeting-type">{meeting.meeting_type?.replace('_', ' ')}</span>
                          <span className={`interest-badge interest-${meeting.client_interest}`}>
                            {meeting.client_interest?.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="meeting-outcome">{meeting.outcome?.replace('_', ' ')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>Close</button>
              <button className="btn btn-primary" onClick={() => { setShowDetailModal(false); handleEditClient(selectedClient); }}>
                <Edit2 size={14} /> Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content edit-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingClient?.id ? 'Edit Client' : 'Add New Client'}</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Company Name *</label>
                  <input
                    type="text"
                    value={editingClient?.company_name || ''}
                    onChange={(e) => setEditingClient(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Enter company name"
                  />
                </div>

                <div className="form-group">
                  <label>Contact Name</label>
                  <input
                    type="text"
                    value={editingClient?.contact_name || ''}
                    onChange={(e) => setEditingClient(prev => ({ ...prev, contact_name: e.target.value }))}
                    placeholder="Enter contact name"
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    value={editingClient?.contact_phone || ''}
                    onChange={(e) => setEditingClient(prev => ({ ...prev, contact_phone: e.target.value }))}
                    placeholder="Enter phone number"
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editingClient?.contact_email || ''}
                    onChange={(e) => setEditingClient(prev => ({ ...prev, contact_email: e.target.value }))}
                    placeholder="Enter email"
                  />
                </div>

                <div className="form-group">
                  <label>Industry</label>
                  <input
                    type="text"
                    value={editingClient?.industry || ''}
                    onChange={(e) => setEditingClient(prev => ({ ...prev, industry: e.target.value }))}
                    placeholder="e.g., Technology, Healthcare"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Address</label>
                  <input
                    type="text"
                    value={editingClient?.address || ''}
                    onChange={(e) => setEditingClient(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Enter full address"
                  />
                </div>

                <div className="form-group">
                  <label>Floor</label>
                  <input
                    type="text"
                    value={editingClient?.floor || ''}
                    onChange={(e) => setEditingClient(prev => ({ ...prev, floor: e.target.value }))}
                    placeholder="e.g., 5th Floor"
                  />
                </div>

                <div className="form-group">
                  <label>Office Number</label>
                  <input
                    type="text"
                    value={editingClient?.office_number || ''}
                    onChange={(e) => setEditingClient(prev => ({ ...prev, office_number: e.target.value }))}
                    placeholder="e.g., 502"
                  />
                </div>

                <div className="form-group">
                  <label>Company Size</label>
                  <select
                    value={editingClient?.company_size || ''}
                    onChange={(e) => setEditingClient(prev => ({ ...prev, company_size: e.target.value || null }))}
                  >
                    <option value="">Select size</option>
                    {SIZE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={editingClient?.status || 'prospect'}
                    onChange={(e) => setEditingClient(prev => ({ ...prev, status: e.target.value }))}
                  >
                    {STATUS_OPTIONS.filter(s => s.value !== 'all').map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Interest Level</label>
                  <select
                    value={editingClient?.interest_level || ''}
                    onChange={(e) => setEditingClient(prev => ({ ...prev, interest_level: e.target.value || null }))}
                  >
                    <option value="">Select interest</option>
                    {INTEREST_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>Notes</label>
                  <textarea
                    value={editingClient?.notes || ''}
                    onChange={(e) => setEditingClient(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveClient} disabled={saving}>
                {saving ? 'Saving...' : 'Save Client'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients;
